import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Box, Layers } from "lucide-react";
import { MaskedLine, Reveal } from "../components/Reveal";
import { BRANDS, OTHER_PRODUCTS } from "../data/products";

const Products = () => {
    useEffect(() => {
        document.title = "Products — Mamta Enterprises";
        const hash = window.location.hash;
        if (hash) {
            setTimeout(() => document.querySelector(hash)?.scrollIntoView({ behavior: "smooth" }), 400);
        }
    }, []);

    return (
        <main data-testid="products-page">
            {/* PAGE HEADER */}
            <section className="pt-32 sm:pt-40 pb-14 sm:pb-20 blueprint-grid spotlight border-b border-border" data-testid="products-header">
                <div className="max-w-7xl mx-auto px-5 sm:px-8">
                    <MaskedLine delay={0.1}>
                        <span className="text-xs uppercase tracking-[0.3em] text-brand" data-testid="products-overline">Wholesale Catalogue</span>
                    </MaskedLine>
                    <h1 className="font-display font-semibold uppercase tracking-tight text-5xl sm:text-7xl text-ink mt-3 leading-[0.95]">
                        <MaskedLine delay={0.25}>Products,</MaskedLine>
                        <MaskedLine delay={0.4}>
                            <span className="text-outline">by brand.</span>
                        </MaskedLine>
                    </h1>
                    <MaskedLine delay={0.55} className="mt-5">
                        <p className="text-muted-foreground max-w-xl text-base sm:text-lg leading-relaxed">
                            Every product below ships in standard wholesale cartons. Enquire with the brand, size and quantity — we handle the rest.
                        </p>
                    </MaskedLine>
                    <Reveal delay={0.65}>
                        <div className="relative mt-12" data-testid="products-intro-banner">
                            <div className="absolute -inset-3 border border-brand/25 translate-x-3 translate-y-3 pointer-events-none" />
                            <div className="bg-white border border-border p-3 sm:p-4 shadow-[0_30px_60px_-24px_rgba(26,26,26,0.25)]">
                                <img
                                    src="/assets/brands-stacked.png"
                                    alt="All four Mamta Enterprises brands — Mamta, Tigerstar, Gripstar and Grey Patta"
                                    className="w-full h-auto object-cover"
                                    data-testid="products-intro-image"
                                />
                            </div>
                            <div className="absolute -bottom-4 left-4 bg-ink text-white px-5 py-2.5 font-display uppercase tracking-[0.2em] text-xs" data-testid="products-intro-badge">
                                The Full Range — One Supplier
                            </div>
                        </div>
                    </Reveal>
                </div>
            </section>

            {/* BRAND SECTIONS */}
            {BRANDS.map((brand, bi) => (
                <section
                    key={brand.id}
                    id={brand.id}
                    data-testid={`brand-section-${brand.id}`}
                    className={`border-b border-border ${bi % 2 === 1 ? "bg-white" : ""}`}
                >
                    <div className="max-w-7xl mx-auto px-5 sm:px-8 py-16 sm:py-24 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
                        {/* Image side */}
                        <Reveal className={`lg:col-span-5 ${bi % 2 === 1 ? "lg:order-2" : ""}`}>
                            <div className="relative" style={{ perspective: 1000 }}>
                                <div className="absolute -inset-3 border border-brand/25 translate-x-3 translate-y-3 pointer-events-none" />
                                <motion.div
                                    className="bg-white border border-border p-4 sm:p-6 shadow-[0_30px_60px_-24px_rgba(26,26,26,0.3)]"
                                    whileHover={{ rotateX: 3, rotateY: bi % 2 === 1 ? 3 : -3 }}
                                    transition={{ duration: 0.4, ease: "easeOut" }}
                                >
                                    <img
                                        src={brand.image}
                                        alt={`${brand.name} product packaging`}
                                        className="w-full aspect-[4/3] object-cover object-center"
                                        data-testid={`brand-image-${brand.id}`}
                                    />
                                </motion.div>
                                <div className="absolute -top-4 -left-1 bg-brand text-white font-display px-4 py-2 text-sm uppercase tracking-[0.2em]" data-testid={`brand-number-${brand.id}`}>
                                    {brand.index}
                                </div>
                                {brand.premium && (
                                    <div className="absolute -top-4 right-3 z-10 bg-ink text-white border border-brand font-display px-4 py-2 text-xs uppercase tracking-[0.2em] flex items-center gap-2" data-testid="premium-badge-greypatta">
                                        <span className="w-1.5 h-1.5 bg-brand rotate-45 inline-block" />
                                        Premium Quality
                                    </div>
                                )}
                            </div>
                        </Reveal>

                        {/* Spec side */}
                        <div className={`lg:col-span-7 ${bi % 2 === 1 ? "lg:order-1" : ""}`}>
                            <Reveal>
                                <span className="text-[11px] uppercase tracking-[0.3em] text-brand">{brand.accent}</span>
                                <h2 className="font-display font-semibold uppercase tracking-tight text-4xl sm:text-5xl text-ink mt-2 flex flex-wrap items-center gap-4" data-testid={`brand-name-${brand.id}`}>
                                    {brand.name}
                                    {brand.premium && (
                                        <span className="bg-brand text-white text-xs font-body font-semibold tracking-[0.2em] px-3 py-1.5" data-testid="premium-label-greypatta">
                                            Premium Quality
                                        </span>
                                    )}
                                </h2>
                                <p className="text-muted-foreground mt-3 text-base leading-relaxed max-w-lg">{brand.tagline}</p>
                            </Reveal>

                            <div className="mt-8 border border-border bg-white" data-testid={`brand-specs-${brand.id}`}>
                                <div className="grid grid-cols-12 px-5 py-3 bg-ink text-white text-[11px] uppercase tracking-[0.2em]">
                                    <span className="col-span-5">Product</span>
                                    <span className="col-span-4">Sizes</span>
                                    <span className="col-span-3 text-right">Packing</span>
                                </div>
                                {brand.products.map((p, pi) => (
                                    <Reveal key={p.name} delay={pi * 0.08}>
                                        <div className="grid grid-cols-12 px-5 py-5 border-t border-border items-start hover:bg-brand-light/40 transition-colors" data-testid={`spec-row-${brand.id}-${pi}`}>
                                            <div className="col-span-5">
                                                <div className="font-display font-medium uppercase tracking-wide text-base text-ink">{p.name}</div>
                                                <div className="flex flex-wrap gap-x-3 gap-y-1 mt-2">
                                                    {p.specs.map((s) => (
                                                        <span key={s} className="text-[11px] text-muted-foreground flex items-center gap-1">
                                                            <span className="w-1.5 h-1.5 bg-brand inline-block" /> {s}
                                                        </span>
                                                    ))}
                                                </div>
                                            </div>
                                            <div className="col-span-4 flex flex-wrap gap-2">
                                                {p.sizes.map((s) => (
                                                    <span key={s} className="border border-ink/20 px-2.5 py-1 text-xs font-medium text-ink" data-testid={`size-chip-${brand.id}-${pi}-${s.replace(/[^a-z0-9]/gi, "")}`}>
                                                        {s}
                                                    </span>
                                                ))}
                                            </div>
                                            <div className="col-span-3 text-right">
                                                <span className="inline-block bg-brand-light text-brand-dark border border-brand/30 px-2.5 py-1 text-xs font-semibold uppercase tracking-wider">
                                                    {p.packing}
                                                </span>
                                            </div>
                                        </div>
                                    </Reveal>
                                ))}
                            </div>

                            <Reveal delay={0.1}>
                                <Link
                                    to={`/contact?product=${encodeURIComponent(brand.name)}`}
                                    data-testid={`brand-enquire-${brand.id}`}
                                    className="group mt-6 inline-flex items-center gap-2 font-display uppercase tracking-[0.15em] text-sm text-ink hover:text-brand transition-colors"
                                >
                                    Enquire about {brand.name}
                                    <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                                </Link>
                            </Reveal>
                        </div>
                    </div>
                </section>
            ))}

            {/* OTHER PRODUCTS */}
            <section className="bg-ink text-white blueprint-grid-dark" data-testid="other-products-section">
                <div className="max-w-7xl mx-auto px-5 sm:px-8 py-16 sm:py-24">
                    <Reveal>
                        <span className="text-xs uppercase tracking-[0.3em] text-brand">Also in stock</span>
                        <h2 className="font-display font-semibold uppercase tracking-tight text-4xl sm:text-5xl mt-2 mb-12">Other products</h2>
                    </Reveal>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-white/15 border border-white/15">
                        {OTHER_PRODUCTS.map((p, i) => (
                            <Reveal key={p.name} delay={i * 0.06} className="bg-ink">
                                <div className="p-7 h-full group hover:bg-ink-soft transition-colors" data-testid={`other-product-${p.name.toLowerCase().replace(/\s+/g, "-")}`}>
                                    <div className="flex items-center justify-between mb-4">
                                        {i % 2 === 0 ? <Box className="w-6 h-6 text-brand" strokeWidth={1.5} /> : <Layers className="w-6 h-6 text-brand" strokeWidth={1.5} />}
                                        <span className="font-display text-white/25 text-sm">0{i + 1}</span>
                                    </div>
                                    <h3 className="font-display font-medium uppercase tracking-wide text-lg">{p.name}</h3>
                                    <p className="text-sm text-white/50 mt-2">{p.desc}</p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                    <Reveal delay={0.2}>
                        <Link
                            to="/contact"
                            data-testid="other-products-enquire-button"
                            className="group mt-10 inline-flex items-center gap-3 bg-brand hover:bg-brand-dark text-white font-display uppercase tracking-[0.15em] text-sm px-8 py-4 transition-colors"
                        >
                            Ask about availability
                            <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                        </Link>
                    </Reveal>
                </div>
            </section>

            {/* FULL-WIDTH BANNER — BRIDGE TO CONTACT */}
            <section className="relative" data-testid="products-power-banner">
                <div className="relative h-[320px] sm:h-[420px] overflow-hidden">
                    <img
                        src="/assets/power-lineup.png"
                        alt="Mamta, Tigerstar, Gripstar and Grey Patta boxes — The Power Behind Stronger Builds"
                        className="absolute inset-0 w-full h-full object-cover object-center"
                        data-testid="products-power-image"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-ink/70 via-transparent to-transparent" />
                    <div className="absolute inset-x-0 bottom-0 pb-8 sm:pb-12">
                        <div className="max-w-7xl mx-auto px-5 sm:px-8 flex flex-col sm:flex-row items-start sm:items-end justify-between gap-6">
                            <Reveal>
                                <p className="text-white font-display font-semibold uppercase tracking-tight text-2xl sm:text-4xl leading-tight drop-shadow-md" data-testid="products-power-text">
                                    Ready to put these<br />on your shelves?
                                </p>
                            </Reveal>
                            <Reveal delay={0.15}>
                                <Link
                                    to="/contact"
                                    data-testid="products-power-contact-button"
                                    className="group inline-flex items-center gap-3 bg-brand hover:bg-brand-dark text-white font-display uppercase tracking-[0.15em] text-sm px-8 py-4 transition-colors shadow-lg"
                                >
                                    Contact Us
                                    <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                                </Link>
                            </Reveal>
                        </div>
                    </div>
                </div>
            </section>
        </main>
    );
};

export default Products;
